import { useStore } from "@tanstack/react-form";
import { Plus } from "lucide-react";

import { withForm } from "~/components/form/app-form";
import { ArrayItemCard } from "~/components/form/fields/array-item-card";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";

import { offerFormOptions } from "../offer-form-options";

// Duration units offered when a non-sprint offer picks its own label. Mini
// Sprints keep their dedicated Day/Week/Module card and don't render this.
const UNIT_OPTIONS = [
  { value: "Module", label: "Module" },
  { value: "Week", label: "Week" },
  { value: "Phase", label: "Phase" },
  { value: "Session", label: "Session" },
  { value: "Day", label: "Day" },
];

export const CurriculumSection = withForm({
  ...offerFormOptions,
  props: {} as {
    title?: string;
    description?: string;
    // When true, renders a duration-unit selector at the top of the card so
    // non-sprint offers can choose their own label (Module / Week / …).
    showUnitSelector?: boolean;
  },
  render: function Render({ form, title, description, showUnitSelector }) {
    const durationLabel =
      useStore(form.store, (s) => s.values.durationLabel) || "Item";
    const unit = durationLabel.toLowerCase();
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title ?? "Curriculum"}</CardTitle>
          <CardDescription>
            {description ??
              `The ${unit}-by-${unit} breakdown shown on your Mini Sprint page.`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {showUnitSelector && (
            <form.AppField name="durationLabel">
              {(field) => (
                <field.SelectField
                  label="Unit"
                  options={UNIT_OPTIONS}
                  placeholder="Module"
                  helpText="What you call each step — shown as the badge on every item."
                />
              )}
            </form.AppField>
          )}
          <form.Field name="curriculum" mode="array">
            {(arrayField) => (
              <>
                {(arrayField.state.value ?? []).map((item, i) => (
                  // Stable React key: persisted rows have `id`, freshly-pushed
                  // rows carry the client-generated `_key`. Using the array
                  // index would let Tiptap editor state drift on reorder.
                  <ArrayItemCard
                    key={item.id ?? item._key ?? `fallback-${i}`}
                    index={i}
                    isFirst={i === 0}
                    isLast={i === (arrayField.state.value?.length ?? 0) - 1}
                    isDeleted={item._delete === true}
                    itemLabel={durationLabel}
                    onMoveUp={() => arrayField.moveValue(i, i - 1)}
                    onMoveDown={() => arrayField.moveValue(i, i + 1)}
                    onToggleDelete={() =>
                      form.setFieldValue(
                        `curriculum[${i}]._delete`,
                        !item._delete,
                      )
                    }
                  >
                    <form.AppField name={`curriculum[${i}].title`}>
                      {(field) => <field.TextField label="Title" required />}
                    </form.AppField>
                    <form.AppField name={`curriculum[${i}].content`}>
                      {(field) => (
                        <field.RichTextField
                          label="What happens"
                          placeholder="Describe this session. Add bullets if you like."
                        />
                      )}
                    </form.AppField>
                  </ArrayItemCard>
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() =>
                    arrayField.pushValue({
                      _key: crypto.randomUUID(),
                      title: "",
                      content: "",
                      order: arrayField.state.value?.length ?? 0,
                      _delete: false,
                    })
                  }
                >
                  <Plus className="mr-2 h-4 w-4" /> Add {durationLabel}
                </Button>
              </>
            )}
          </form.Field>
        </CardContent>
      </Card>
    );
  },
});
